import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  fetchMe,
  isTwoFactorChallenge,
  login as apiLogin,
  logout as apiLogout,
  restoreSession,
  setAccessToken,
  verifyTwoFactor,
  type Me,
  type TwoFactorChallenge,
} from "@/api/client";

/** Verify succeeded but /me could not be read, so the user is half signed in. */
export class AccountLoadError extends Error {
  constructor(cause: unknown) {
    super("Signed in, but the account could not be loaded", { cause });
    this.name = "AccountLoadError";
  }
}

export interface SessionValue {
  me: Me | null;
  loading: boolean;
  /** Resolves to a challenge when the account needs an emailed code, else null once signed in. */
  signIn: (email: string, password: string) => Promise<TwoFactorChallenge | null>;
  completeTwoFactor: (challenge: string, code: string, remember: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-reads /me, so the nav and guards follow a change to the signed-in user's own roles. */
  refresh: () => Promise<void>;
  /**
   * Mirrors the server's RBAC check so the UI can hide what a user cannot do (07.1).
   * This is presentation only — every handler re-checks server-side, and a client that
   * lies to itself just gets a 403.
   */
  can: (permission: string) => boolean;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    restoreSession()
      .then((session) => {
        if (!cancelled) setMe(session);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const queryClient = useQueryClient();

  // A 401 that survived the client's refresh attempt means the session is gone; dropping `me`
  // lets the route guard send the user to sign in and bring them back afterwards.
  useEffect(() => {
    const expireOn401 = (error: unknown) => {
      if (!(error instanceof ApiError) || error.status !== 401) return;
      setAccessToken(null);
      setMe(null);
    };
    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error") expireOn401(event.action.error);
    });
    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error") expireOn401(event.action.error);
    });
    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [queryClient]);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await apiLogin(email, password);
    if (isTwoFactorChallenge(result)) return result;
    setMe(await fetchMe());
    return null;
  }, []);

  const completeTwoFactor = useCallback(async (challenge: string, code: string, remember: boolean) => {
    await verifyTwoFactor(challenge, code, remember);
    try {
      setMe(await fetchMe());
    } catch {
      // The code is spent, so a transient /me failure gets one retry rather than a dead end.
      try {
        setMe(await fetchMe());
      } catch (err) {
        throw new AccountLoadError(err);
      }
    }
  }, []);

  // Whichever way a session ends, the next person at this browser must not see its cached data.
  useEffect(() => {
    if (me === null) queryClient.clear();
  }, [me, queryClient]);

  const signOut = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // The server may keep the refresh cookie, but this browser still stops acting as the user.
    }
    setMe(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      setMe(await fetchMe());
    } catch (error) {
      // A 401 means the session is gone; anything else keeps the current session and nav.
      if (error instanceof ApiError && error.status === 401) {
        setAccessToken(null);
        setMe(null);
      }
    }
  }, []);

  const permissions = useMemo(() => new Set(me?.permissions ?? []), [me]);
  const can = useCallback((permission: string) => permissions.has(permission), [permissions]);

  const value = useMemo(
    () => ({ me, loading, signIn, completeTwoFactor, signOut, refresh, can }),
    [me, loading, signIn, completeTwoFactor, signOut, refresh, can],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside a SessionProvider");
  return ctx;
}
